'use client';

import { useState, useEffect } from 'react';
import { ArrowRight, Play, Building2, Users, Clock, Shield } from 'lucide-react';
import Link from 'next/link';
import { cn } from '@/lib/utils';

const Hero = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
      {/* Modern Gradient Mesh Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Primary blue blob - large, center-right */}
        <div
          className={cn(
            "absolute top-1/2 right-1/4 -translate-y-1/2 w-[800px] h-[800px]",
            "bg-gradient-radial from-primary-500/8 dark:from-primary-500/25 via-primary-600/10 to-transparent",
            "rounded-full blur-3xl",
            "transition-all duration-1000 ease-out",
            isLoaded ? "opacity-100 scale-100" : "opacity-0 scale-95"
          )}
          style={{
            animation: isLoaded ? 'blob-drift 20s ease-in-out infinite' : 'none',
          }}
        />

        {/* Secondary purple/indigo blob - top-left */}
        <div
          className={cn(
            "absolute -top-1/4 -left-1/4 w-[600px] h-[600px]",
            "bg-gradient-radial from-indigo-500/5 dark:from-indigo-500/20 via-purple-600/10 to-transparent",
            "rounded-full blur-3xl",
            "transition-all duration-1000 ease-out",
            isLoaded ? "opacity-100 scale-100" : "opacity-0 scale-95"
          )}
          style={{
            transitionDelay: '200ms',
            animation: isLoaded ? 'blob-drift-reverse 25s ease-in-out infinite' : 'none',
          }}
        />

        {/* Accent cyan blob - bottom */}
        <div
          className={cn(
            "absolute -bottom-1/4 left-1/3 w-[500px] h-[500px]",
            "bg-gradient-radial from-cyan-500/5 dark:from-cyan-500/15 via-blue-500/8 to-transparent",
            "rounded-full blur-3xl",
            "transition-all duration-1000 ease-out",
            isLoaded ? "opacity-100 scale-100" : "opacity-0 scale-95"
          )}
          style={{
            transitionDelay: '400ms',
            animation: isLoaded ? 'blob-drift 30s ease-in-out infinite reverse' : 'none',
          }}
        />

        {/* Subtle mesh overlay for depth - dark mode only (reduced to let watermark show) */}
        <div
          className={cn(
            "absolute inset-0 hidden dark:block",
            "bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(13,13,13,0.2)_70%,rgba(13,13,13,0.4)_100%)]"
          )}
        />

        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(to right, #3b82f6 1px, transparent 1px),
              linear-gradient(to bottom, #3b82f6 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px'
          }}
        />

        {/* Noise texture for grain */}
        <div className="absolute inset-0 noise opacity-[0.015]" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-24 md:pt-32 pb-20">
        <div className="text-center">
          {/* Brand Tagline */}
          <p
            className={cn(
              "text-base sm:text-lg font-brand italic tracking-wide text-gray-600 dark:text-secondary-300 mb-4",
              "transition-all duration-700 ease-out",
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '100ms' }}
          >
            Where Operations Align.
          </p>

          {/* Main Headline */}
          <h1
            className={cn(
              "font-playfair text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-4 sm:mb-6",
              "transition-all duration-700 ease-out",
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '200ms' }}
          >
            <span className="text-gray-900 dark:text-secondary-100">One Platform.</span>
            <br />
            <span className="bg-gradient-to-r from-primary-400 via-primary-300 to-primary-400 bg-clip-text text-transparent">
              All Your Properties.
            </span>
          </h1>

          {/* Subheadline */}
          <p
            className={cn(
              "max-w-2xl mx-auto text-base sm:text-lg md:text-xl text-gray-500 dark:text-secondary-400 mb-8 sm:mb-10 leading-relaxed",
              "transition-all duration-700 ease-out",
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '300ms' }}
          >
            Manage every property type from a single dashboard.
            Work orders, vendors, maintenance — all in one place.
          </p>

          {/* CTAs */}
          <div
            className={cn(
              "flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-10 sm:mb-16",
              "transition-all duration-700 ease-out",
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '400ms' }}
          >
            <a
              href="#contact"
              className={cn(
                "group relative inline-flex items-center gap-2 px-6 py-3 sm:px-8 sm:py-4 rounded-xl",
                "bg-primary-500 text-white font-semibold",
                "shadow-lg dark:shadow-[0_0_40px_-10px_rgba(59,130,246,0.5)]",
                "hover:shadow-xl dark:hover:shadow-[0_0_60px_-10px_rgba(59,130,246,0.6)]",
                "hover:bg-primary-400 hover:scale-[1.02]",
                "animate-glow-pulse",
                "transition-all duration-300"
              )}
            >
              Join the Waitlist
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>

            <Link
              href="/how-it-works"
              className={cn(
                "group inline-flex items-center gap-2 px-6 py-3 sm:px-8 sm:py-4 rounded-xl",
                "bg-white dark:bg-white/[0.03] backdrop-blur-sm border border-gray-200 dark:border-white/[0.08] text-gray-700 dark:text-secondary-200",
                "hover:bg-gray-50 dark:hover:bg-white/[0.06] hover:border-gray-300 dark:hover:border-white/[0.15]",
                "font-medium transition-all duration-300"
              )}
            >
              <Play className="w-4 h-4" />
              See How It Works
            </Link>
          </div>

          {/* Stats Row - Glass Cards */}
          <div
            className={cn(
              "grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 max-w-4xl mx-auto",
              "transition-all duration-700 ease-out",
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '500ms' }}
          >
            {[
              { icon: Building2, value: '74+', label: 'Property Types' },
              { icon: Shield, value: '6', label: 'Built-in Departments' },
              { icon: Clock, value: '$49', label: 'Starting /mo' },
              { icon: Users, value: '24/7', label: 'Full Visibility' },
            ].map((stat, i) => (
              <div
                key={i}
                className={cn(
                  "group p-3 sm:p-5 rounded-2xl",
                  "bg-white dark:bg-white/[0.02] backdrop-blur-sm border border-gray-200 dark:border-white/[0.06]",
                  "hover:bg-gray-50 dark:hover:bg-white/[0.04] hover:border-primary-500/30",
                  "hover:shadow-lg dark:hover:shadow-[0_0_30px_-10px_rgba(59,130,246,0.3)]",
                  "transition-all duration-300"
                )}
              >
                <stat.icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600 dark:text-primary-400 mx-auto mb-1.5 sm:mb-2 group-hover:scale-110 transition-transform" />
                <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-secondary-100 font-playfair">{stat.value}</div>
                <div className="text-[10px] sm:text-xs text-gray-400 dark:text-secondary-500 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
          {/* LiDAR Teaser */}
          <div
            className={cn(
              "flex justify-center mt-8",
              "transition-all duration-700 ease-out",
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '600ms' }}
          >
            <div className={cn(
              "inline-flex items-center gap-2 px-4 py-2 rounded-full",
              "bg-white/60 dark:bg-white/[0.02] backdrop-blur-sm border border-gray-200 dark:border-white/[0.06]",
              "text-xs text-gray-400 dark:text-secondary-500"
            )}>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Coming soon: LiDAR-powered property scanning
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator — hidden on small mobile */}
      <div
        className={cn(
          "absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:block",
          "transition-all duration-700 ease-out",
          isLoaded ? "opacity-100" : "opacity-0"
        )}
        style={{ transitionDelay: '800ms' }}
      >
        <div className="w-6 h-10 rounded-full border border-gray-300 dark:border-white/20 flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-primary-600 dark:bg-primary-400 rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export { Hero };
